import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import { GetSupportQueue, SupportTicket } from '../../services/dtos';
import { usePortalAccess } from '../providers/portalAccess';
import {
  TicketAgeDot,
  TicketCategoryBadge,
  TicketEscalatedBadge,
  TicketStatusBadge,
  TicketUrgentBadge,
  fmtDateTime,
  visibleLanes,
} from './supportShared';

// One tab per lane the viewer can list: their queue roles, the
// FrontDesk↔Dispatch mutual pair (T5), the BR/ABR escalation lanes, and
// every lane for admins. Delegated lanes arrive via /portal/access for the
// date range, so a covering person sees the tab exactly while they cover.
// Tab visibility is cosmetic — the server 403s a lane the caller can't list,
// and confidential tickets drop out server-side.
const SupportQueuePage: React.FC = () => {
  const { access, hasPortalRole } = usePortalAccess();

  const myLanes = useMemo(() => visibleLanes(access?.roles || []), [access]);

  const [queueName, setQueueName] = useState<string | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // pick the first available tab once roles arrive
  useEffect(() => {
    if (!queueName && myLanes.length > 0) setQueueName(myLanes[0].name);
  }, [myLanes, queueName]);

  useEffect(() => {
    if (!queueName) return;
    setLoading(true);
    setError(null);
    const req = new GetSupportQueue();
    req.name = queueName;
    req.includeDismissed = includeDismissed;
    apiServices.client
      .get(req)
      .then((data: SupportTicket[]) => {
        setTickets(data);
        setLoading(false);
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to load the queue');
        setLoading(false);
      });
  }, [queueName, includeDismissed]);

  const activeLabel =
    myLanes.find((q) => q.name === queueName)?.label || queueName;

  return (
    <div className="mx-auto w-full flex max-w-6xl flex-col gap-6 dark:text-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Support Queue — {activeLabel}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Urgent first, then oldest. Grab a ticket to take it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to="/help/board"
            className="text-sm font-medium text-indigo-600 dark:text-indigo-400"
          >
            Board →
          </Link>
          <Link
            to="/help/snippets"
            className="text-sm font-medium text-indigo-600 dark:text-indigo-400"
          >
            Snippets →
          </Link>
          <Link
            to="/help/delegations"
            className="text-sm font-medium text-indigo-600 dark:text-indigo-400"
          >
            Out of office →
          </Link>
          {/* T7: the conduct-controls screen (terms + bans) for BanAuthority */}
          {hasPortalRole('BanAuthority') && (
            <Link
              to="/help/admin"
              className="text-sm font-medium text-indigo-600 dark:text-indigo-400"
            >
              Conduct controls →
            </Link>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeDismissed}
              onChange={(e) => setIncludeDismissed(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Show dismissed
          </label>
        </div>
      </div>

      {myLanes.length > 1 && (
        <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          {myLanes.map((q) => (
            <button
              key={q.name}
              onClick={() => setQueueName(q.name)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                q.name === queueName
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {q.label}
            </button>
          ))}
        </div>
      )}

      {error && <Toast color="failure">{error}</Toast>}
      {loading && <Spinner color="info" />}

      {!loading && !error && tickets.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          The queue is empty. 🎉
        </div>
      )}

      {tickets.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-3 py-2 w-14">#</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2 w-40">Type</th>
                <th className="px-3 py-2 w-40">From</th>
                <th className="px-3 py-2 w-44">Show</th>
                <th className="px-3 py-2 w-28">Status</th>
                <th className="px-3 py-2 w-36">Owner</th>
                <th className="px-3 py-2 w-32">Waiting</th>
                <th className="px-3 py-2 w-40">Opened</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {tickets.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2 text-gray-400 text-xs">{t.id}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/help/ticket/${t.id}`}
                        className="font-medium text-indigo-600 dark:text-indigo-400 break-words"
                      >
                        {t.subject}
                      </Link>
                      <TicketUrgentBadge isUrgent={t.isUrgent} />
                      <TicketEscalatedBadge toRole={t.escalatedToRole} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <TicketCategoryBadge category={t.category} />
                  </td>
                  <td className="px-3 py-2">
                    {/* S1: the member's running record */}
                    <Link
                      to={`/help/member/${t.memberUniqueMemberId}`}
                      className="text-gray-700 hover:text-indigo-600 hover:underline dark:text-gray-300 dark:hover:text-indigo-400"
                    >
                      {t.memberName}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 break-words">
                    {t.showSnapshot
                      ? [t.showSnapshot, t.siteSnapshot].filter(Boolean).join(' — ')
                      : t.relatedShowFreeText || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <TicketStatusBadge status={t.status} />
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    {t.ownerName || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <TicketAgeDot ticket={t} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {fmtDateTime(t.createdAtUtc)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupportQueuePage;
